import React from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import { router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface LifecycleDay {
  date: string
  created: number
  worked_on: number
  reviewed: number
  completed: number
  accepted: number
}

interface CycleTimeTask {
  task_id: number
  summary: string
  status: string
  pending_to_review_hours: number | null
  review_to_completed_hours: number | null
  total_hours: number | null
  completed_at: string | null
}

interface CycleTimeSummary {
  count: number
  avg_pending_to_review: number | null
  avg_review_to_completed: number | null
  avg_total: number | null
  median_pending_to_review: number | null
  median_review_to_completed: number | null
  median_total: number | null
}

interface CycleTimeData {
  summary: CycleTimeSummary
  tasks: CycleTimeTask[]
}

interface LeaderboardEntry {
  agent_id: number
  username: string
  slug: string
  agent_type: string
  tickets_worked: number
  tickets_completed: number
  sessions_per_day: number
  sessions_per_ticket: number
  entries_per_ticket: number
}

interface FilterOption {
  id: number
  name?: string
  username?: string
}

interface Filters {
  projects: FilterOption[]
  agents: FilterOption[]
  selected_project_id: number | null
  selected_agent_id: number | null
}

interface Props {
  task_lifecycle: LifecycleDay[]
  cycle_time: CycleTimeData
  agent_leaderboard: LeaderboardEntry[]
  filters: Filters
}

type SortField = 'tickets_worked' | 'tickets_completed' | 'sessions_per_day' | 'sessions_per_ticket' | 'entries_per_ticket'

function formatHours(hours: number | null): string {
  if (hours === null) return '-'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 24) return `${hours}h`
  const days = (hours / 24).toFixed(1)
  return `${days}d`
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function BarCell({ value, max, color }: { value: number; max: number; color: string }) {
  if (value === 0) return <TableCell align="center"><Typography variant="body2" color="text.secondary">0</Typography></TableCell>
  const pct = max > 0 ? (value / max) * 100 : 0
  return (
    <TableCell align="center">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
        <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'right' }}>{value}</Typography>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            flex: 1,
            height: 8,
            borderRadius: 4,
            backgroundColor: 'action.hover',
            '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 },
          }}
        />
      </Box>
    </TableCell>
  )
}

export default function Show({ task_lifecycle, cycle_time, agent_leaderboard, filters }: Props) {
  const [sortField, setSortField] = React.useState<SortField>('tickets_completed')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc')

  const handleFilterChange = (key: string, value: string) => {
    const params: Record<string, string> = {}
    if (key === 'project_id') {
      if (value) params.project_id = value
      if (filters.selected_agent_id) params.agent_id = String(filters.selected_agent_id)
    } else {
      if (filters.selected_project_id) params.project_id = String(filters.selected_project_id)
      if (value) params.agent_id = value
    }
    router.get('/monitoring/statistics', params, { preserveState: true })
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const sortedLeaderboard = [...agent_leaderboard].sort((a, b) => {
    const diff = a[sortField] - b[sortField]
    return sortDir === 'asc' ? diff : -diff
  })

  // Compute max values for bar charts
  const maxCreated = Math.max(...task_lifecycle.map(d => d.created), 1)
  const maxWorked = Math.max(...task_lifecycle.map(d => d.worked_on), 1)
  const maxReviewed = Math.max(...task_lifecycle.map(d => d.reviewed), 1)
  const maxCompleted = Math.max(...task_lifecycle.map(d => d.completed), 1)
  const maxAccepted = Math.max(...task_lifecycle.map(d => d.accepted), 1)

  // Only show rows with data or last 14 days minimum
  const recentLifecycle = task_lifecycle.slice(-14)

  return (
    <SidebarLayout section="monitoring" title="Statistics">
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filters.selected_project_id ? String(filters.selected_project_id) : ''}
            label="Project"
            onChange={(e) => handleFilterChange('project_id', e.target.value as string)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {filters.projects.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Agent</InputLabel>
          <Select
            value={filters.selected_agent_id ? String(filters.selected_agent_id) : ''}
            label="Agent"
            onChange={(e) => handleFilterChange('agent_id', e.target.value as string)}
          >
            <MenuItem value="">All Agents</MenuItem>
            {filters.agents.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>@{a.username}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Task Lifecycle */}
      <Typography variant="h6" sx={{ mb: 2 }}>Task Lifecycle (Last 14 Days)</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 4, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="center">Worked On</TableCell>
              <TableCell align="center">Reviewed</TableCell>
              <TableCell align="center">Completed</TableCell>
              <TableCell align="center">Accepted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentLifecycle.map((day) => (
              <TableRow key={day.date}>
                <TableCell>
                  <Typography variant="body2">{formatDate(day.date)}</Typography>
                </TableCell>
                <BarCell value={day.created} max={maxCreated} color="#3B82F6" />
                <BarCell value={day.worked_on} max={maxWorked} color="#8B5CF6" />
                <BarCell value={day.reviewed} max={maxReviewed} color="#F59E0B" />
                <BarCell value={day.completed} max={maxCompleted} color="#10B981" />
                <BarCell value={day.accepted} max={maxAccepted} color="#059669" />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cycle Time */}
      <Typography variant="h6" sx={{ mb: 2 }}>Cycle Time</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Avg Pending to Review', avg: cycle_time.summary.avg_pending_to_review, median: cycle_time.summary.median_pending_to_review },
          { label: 'Avg Review to Completed', avg: cycle_time.summary.avg_review_to_completed, median: cycle_time.summary.median_review_to_completed },
          { label: 'Avg Total', avg: cycle_time.summary.avg_total, median: cycle_time.summary.median_total },
        ].map((item) => (
          <Grid size={{ xs: 12, sm: 4 }} key={item.label}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>{item.label}</Typography>
                <Typography variant="h5" fontWeight={600}>{formatHours(item.avg)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Median: {formatHours(item.median)} | {cycle_time.summary.count} tasks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {cycle_time.tasks.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 4, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell align="center">Pending to Review</TableCell>
                <TableCell align="center">Review to Completed</TableCell>
                <TableCell align="center">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cycle_time.tasks.map((task) => (
                <TableRow key={task.task_id}>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                      #{task.task_id} {task.summary}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{formatHours(task.pending_to_review_hours)}</TableCell>
                  <TableCell align="center">{formatHours(task.review_to_completed_hours)}</TableCell>
                  <TableCell align="center">{formatHours(task.total_hours)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Agent Leaderboard */}
      <Typography variant="h6" sx={{ mb: 2 }}>Agent Leaderboard (Last 30 Days)</Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              {([
                ['tickets_worked', 'Tickets Worked'],
                ['tickets_completed', 'Tickets Completed'],
                ['sessions_per_day', 'Sessions/Day'],
                ['sessions_per_ticket', 'Sessions/Ticket'],
                ['entries_per_ticket', 'Entries/Ticket'],
              ] as [SortField, string][]).map(([field, label]) => (
                <TableCell align="center" key={field}>
                  <TableSortLabel
                    active={sortField === field}
                    direction={sortField === field ? sortDir : 'desc'}
                    onClick={() => handleSort(field)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLeaderboard.map((entry) => (
              <TableRow key={entry.agent_id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>@{entry.username}</Typography>
                </TableCell>
                <TableCell align="center">{entry.tickets_worked}</TableCell>
                <TableCell align="center">{entry.tickets_completed}</TableCell>
                <TableCell align="center">{entry.sessions_per_day}</TableCell>
                <TableCell align="center">{entry.sessions_per_ticket}</TableCell>
                <TableCell align="center">{entry.entries_per_ticket}</TableCell>
              </TableRow>
            ))}
            {sortedLeaderboard.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No agent activity in the last 30 days.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SidebarLayout>
  )
}
